import { useLocation, useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import Header from "@/components/Header";
import PropertyGallery from "@/components/PropertyGallery";
import { Property } from "@/types/property";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { isUnauthorizedError } from "@/lib/authUtils";
import API_URL from "@/config";
import axios from "axios";
import { useSelector } from "react-redux";
import useDocumentTitle from "@/components/Helmet";
import LocationView from "@/components/GoogleMap/LocationView";
import { Box } from "@mui/material";
import axiosInstance from "@/lib/axiosInstance";
import { formatPrice } from "@/lib/utils";
import { Home, MapPin, MessageCircle, Share } from "lucide-react";

export default function PropertyDetail() {
  useDocumentTitle("Property Detail");
  const { id } = useParams();
  const [_, navigate] = useLocation()
  const { user } = useSelector((state: any) => state.auth);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const { data: property, isLoading } = useQuery<Property>({
    queryKey: ["/api/properties", id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/api/properties/${id}`);
      return res.data?.data;
    },
    enabled: !!id,
  });

  const { data: isFavoriteData } = useQuery<{ isFavorite: boolean }>({
    queryKey: ["/api/favorites", id, "status"],
    queryFn: async () => {
      const res = await axiosInstance.get(API_URL + `/api/favorites/${id}/status`);
      return res.data;
    },
    enabled: !!user && !!id,
  });

  const favoriteMutation = useMutation({
    mutationFn: async (action: 'add' | 'remove') => {
      if (action === 'add') {
        return await axiosInstance.post("/api/favorites", { propertyId: id });
      } else {
        return await axiosInstance.delete(`/api/favorites/${id}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/favorites"] });
      queryClient.invalidateQueries({ queryKey: ["/api/favorites", id, "status"] });
      toast({
        title: "Success",
        description: isFavoriteData?.isFavorite ? "Removed from favorites" : "Added to favorites",
      });
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to update favorites",
        variant: "destructive",
      });
    },
  });

  const inquiryMutation = useMutation({
    mutationFn: async (inquiryData: typeof inquiryForm) => {
      return await axiosInstance.post("/api/inquiries", {
        ...inquiryData,
        propertyId: id,
      });
    },
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Success",
        description: "Your inquiry has been sent successfully",
      });
      setInquiryForm({ name: "", email: "", phone: "", message: "" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to send inquiry",
        variant: "destructive",
      });
    },
  });

  const handleFavoriteToggle = () => {
    if (!user) {
      window.location.href = "/api/login";
      return;
    }
    favoriteMutation.mutate(isFavoriteData?.isFavorite ? 'remove' : 'add');
  };

  const handleInquirySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inquiryMutation.mutate(inquiryForm);
  };``

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: property?.title,
          text: `Check out this property: ${property?.title}`,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // fallback if Web Share API is not supported
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copied!",
        description: "Property link copied to clipboard.",
      });
    }
  };

  const sendMessage = async() => {
    if(!user){
      navigate('/login')
    }
    try {
      await axiosInstance.post("/api/chat/conversations", {
        propertyId: id,
        participantId: property?.owner
      })
      .then((res) => {
        if(res.data.success){
          navigate(`/buyer/chat/${res.data.data._id}`)
        }
        else{
          toast({
            title: "Error",
            description: "Failed to create conversation",
            variant: "destructive",
          });
        }
      })
    } catch (error :any) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to create conversation",
        variant: "destructive",
      }) 
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-96 bg-gray-200 rounded-lg mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-32 bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-4">
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Property not found</h1>
          <Button onClick={() => window.location.href = "/"} data-testid="button-back-home">
            Back to Properties
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <PropertyGallery images={property.images} videos={property.videos} title={property.title} />

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex justify-between items-start mb-4">
                <div className="mr-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2 flex gap-2" data-testid="text-property-price">
                    {formatPrice(property.price)} &nbsp; &nbsp; 
                    <Button onClick={handleShare}>
                      <Share className="w-5 h-5" />
                    </Button>
                    <Button onClick={sendMessage}>
                      <MessageCircle className="w-5 h-5" />
                    </Button>
                  </h1>
                  <p className="text-lg text-gray-600 flex gap-2" data-testid="text-property-title">
                    <Home /> {property.title}
                  </p>
                  <p className="text-gray-600 mt-1 flex gap-2" data-testid="text-property-address">
                    <MapPin />  {property.address}, {property.city}, {property.country}
                  </p>
                </div>
                {user?.userType == "buyer" && <Button
                  variant="outline"
                  onClick={handleFavoriteToggle}
                  disabled={favoriteMutation.isPending}
                  data-testid={`button-${isFavoriteData?.isFavorite ? 'remove' : 'add'}-favorite`}
                >
                  <i className={`${isFavoriteData?.isFavorite ? 'fas' : 'far'} fa-heart mr-2 ${isFavoriteData?.isFavorite ? 'text-red-500' : ''}`}></i>
                  {isFavoriteData?.isFavorite ? 'Remove' : 'Save'}
                </Button>}
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <i className="fas fa-bed text-2xl text-primary mb-2"></i>
                  <p className="text-sm text-gray-600">Bedrooms</p>
                  <p className="font-semibold" data-testid="text-bedrooms">{property.beds || 'N/A'}</p>
                </div>
                <div className="text-center">
                  <i className="fas fa-bath text-2xl text-primary mb-2"></i>
                  <p className="text-sm text-gray-600">Bathrooms</p>
                  <p className="font-semibold" data-testid="text-bathrooms">{property.baths || 'N/A'}</p>
                </div>
                <div className="text-center">
                  <i className="fas fa-ruler-combined text-2xl text-primary mb-2"></i>
                  <p className="text-sm text-gray-600">Floor Area</p>
                  <p className="font-semibold" data-testid="text-floor-area">
                    {property.size ? `${property.size} m²` : 'N/A'}
                  </p>
                </div>
                <div className="text-center">
                  <i className="fas fa-home text-2xl text-primary mb-2"></i>
                  <p className="text-sm text-gray-600">Property Type</p>
                  <p className="font-semibold capitalize" data-testid="text-property-type">
                    {property.propertyType}
                  </p>
                </div>
              </div>

              {/* Description */}
              {property.description && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-3">Property Description</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-testid="text-description">
                    {property.description}
                  </p>
                </div>
              )}

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-3">Key Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-gray-700">
                        <i className="fas fa-check text-secondary mr-2"></i>
                        <span data-testid={`text-feature-${index}`}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Map Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Location</h3>
              {property.location ? <Box>
                <LocationView location={property.location} />
              </Box> : <div className="bg-gray-200 h-64 rounded-lg flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <i className="fas fa-map-marked-alt text-4xl mb-2"></i>
                  <p>Interactive Map</p>
                  <p className="text-sm">{property.address}, {property.city}</p>
                </div>
              </div>}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Agent Contact */}
            {property.agent && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6 sticky top-24">
                <div className="flex items-center mb-4">
                  {property.agent.image ? (
                    <img
                      src={property.agent.image}
                      alt={property.agent.name}
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                      <i className="fas fa-user text-gray-400"></i>
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900 capitalize" data-testid="text-agent-name">
                      {property.agent.name}
                    </h4>
                    <p className="text-sm text-gray-600">Licensed Estate Agent</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {property.agent?.phone && (
                    <Button className="w-full" data-testid="button-call-agent">
                      <i className="fas fa-phone mr-2"></i>Call Now
                    </Button>
                  )}
                  {property.agent?.email && (
                    <Button variant="outline" className="w-full" data-testid="button-email-agent">
                      <i className="fas fa-envelope mr-2"></i>Send Email
                    </Button>
                  )}
                </div>

                {/* Contact Form */}
                <form onSubmit={handleInquirySubmit} className="space-y-4">
                  <h5 className="font-medium text-gray-900">Send a Message</h5>
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={inquiryForm.name}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="input-inquiry-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    data-testid="input-inquiry-email"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={inquiryForm.phone}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                    data-testid="input-inquiry-phone"
                  />
                  <Textarea
                    placeholder="Your Message"
                    rows={3}
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                    data-testid="textarea-inquiry-message"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gray-900 hover:bg-gray-800"
                    disabled={inquiryMutation.isPending}
                    data-testid="button-send-inquiry"
                  >
                    {inquiryMutation.isPending ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
